/**
 * "Onthouden" (ADR 0027): short things to remember, with or without a reminder.
 * Each item is a page in the hidden "Onthouden" folder, so it syncs, is found by
 * search and the AI, and can hold more text. Properties:
 * - `Herinnering`: when to remind ("2026-10-13 09:00"), optional;
 * - `Herhaling`: a recurrence rule ("elke week"); the reminder then moves on
 *   to the next date each time it fires or is marked done;
 * - `Vastgepind`: shown at the top.
 * Reminders go through the same cycle as task reminders (no status, no due date).
 */
import {
  expandCapture,
  nextOccurrence,
  parseTaskText,
  splitDateTime,
  toIsoDate,
  type ReminderTask,
} from '@nexus/core';
import type { PageRecord, Store } from '@nexus/store';
import { ensureSpecialPage } from './tasks';

export const MEMOS_KEY = 'memos_parent_id';
export const MEMO_REMINDER = 'Herinnering';
export const MEMO_RECURRENCE = 'Herhaling';
export const MEMO_PINNED = 'Vastgepind';

/** Time used when a reminder has only a date. */
export const MEMO_DEFAULT_TIME = '09:00';

export const memosFolder = (store: Store) =>
  ensureSpecialPage(store, MEMOS_KEY, 'Onthouden', '📌', { anywhere: true });

export interface Memo {
  id: string;
  title: string;
  /** Next reminder, "YYYY-MM-DDTHH:mm", or null. */
  remindAt: string | null;
  recurrence: string | null;
  pinned: boolean;
  tags: string[];
  createdAt: number;
  updatedAt: number;
}

/** "2026-10-13 9:00", "2026-10-13T09:00" or a date → "2026-10-13T09:00". */
export function normalizeRemindAt(value: unknown): string | null {
  const first = Array.isArray(value) ? value[0] : value;
  if (first instanceof Date) return `${toIsoDate(first)}T${MEMO_DEFAULT_TIME}`;
  if (typeof first !== 'string' || !first.trim()) return null;
  const dt = splitDateTime(first.trim());
  if (!dt) return null;
  const time = dt.time ?? MEMO_DEFAULT_TIME;
  return `${dt.date}T${time.length === 4 ? `0${time}` : time}`;
}

const prop = (props: Record<string, unknown>, key: string) => {
  const hit = Object.keys(props).find((k) => k.toLowerCase() === key.toLowerCase());
  return hit === undefined ? undefined : props[hit];
};

export async function loadMemos(store: Store): Promise<Memo[]> {
  const folder = await store.getSetting(MEMOS_KEY);
  if (!folder) return [];
  const pages = await store.listPagesWithProps();
  return pages
    .filter((p) => p.parentId === folder)
    .map((p) => {
      const tags = prop(p.props, 'tags');
      const rule = prop(p.props, MEMO_RECURRENCE);
      return {
        id: p.id,
        title: p.title,
        remindAt: normalizeRemindAt(prop(p.props, MEMO_REMINDER)),
        recurrence: typeof rule === 'string' && rule.trim() ? rule.trim() : null,
        pinned: prop(p.props, MEMO_PINNED) === true,
        tags: Array.isArray(tags) ? tags.map(String) : [],
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
      };
    });
}

/** Memos with a reminder, in the shape the reminder rules expect. */
export function memoReminderTasks(memos: Memo[]): (ReminderTask & { pageId: string })[] {
  return memos
    .filter((m) => m.remindAt)
    .map((m) => ({
      id: m.id,
      pageId: m.id,
      title: m.title,
      status: 'todo' as const,
      priority: null,
      due: null,
      reminders: [m.remindAt!],
      relativeReminders: [],
      projectId: null,
      tags: m.tags,
    }));
}

/**
 * Quick add: `Garage bellen @morgen 9u` → "Garage bellen", reminder tomorrow 9:00.
 * Without a date there is no reminder.
 */
export function memoFromCapture(text: string, today: string) {
  const parsed = parseTaskText(expandCapture(text.trim(), today));
  const remindAt = parsed.due
    ? `${parsed.due}T${parsed.dueTime ?? MEMO_DEFAULT_TIME}`
    : (parsed.reminders[0] ?? null);
  return {
    title: parsed.title.replace(/\s+/g, ' ').trim() || text.trim(),
    remindAt: remindAt && remindAt.length === 10 ? `${remindAt}T${MEMO_DEFAULT_TIME}` : remindAt,
    recurrence: parsed.recurrence,
  };
}

export async function createMemo(
  store: Store,
  input: { title: string; remindAt?: string | null; recurrence?: string | null },
): Promise<PageRecord> {
  const folder = await memosFolder(store);
  const page = await store.createPage({ title: input.title, parentId: folder.id });
  if (input.remindAt) await setMemoReminder(store, page.id, input.remindAt);
  if (input.recurrence) await store.setProp(page.id, MEMO_RECURRENCE, input.recurrence);
  return page;
}

/** Store as "2026-10-13 09:00" (readable in Markdown and the properties panel); null removes it. */
export async function setMemoReminder(store: Store, id: string, remindAt: string | null) {
  await store.setProp(id, MEMO_REMINDER, remindAt ? remindAt.replace('T', ' ') : undefined);
}

export async function setMemoPinned(store: Store, id: string, pinned: boolean) {
  await store.setProp(id, MEMO_PINNED, pinned || undefined);
}

export async function setMemoRecurrence(store: Store, id: string, rule: string | null) {
  await store.setProp(id, MEMO_RECURRENCE, rule || undefined);
}

/**
 * The reminder fired or was marked done: a recurring memo moves on to its next
 * date after `now` (same time); a one-off reminder is removed. The memo stays.
 */
export async function finishMemoReminder(store: Store, id: string, now = Date.now()) {
  const memo = (await loadMemos(store)).find((m) => m.id === id);
  if (!memo?.remindAt) return;
  const [date, time] = memo.remindAt.split('T') as [string, string];
  let next = memo.recurrence ? nextOccurrence(memo.recurrence, date) : null;
  const today = toIsoDate(new Date(now));
  for (let i = 0; next && next < today && i < 1000; i++)
    next = nextOccurrence(memo.recurrence!, next);
  await setMemoReminder(store, id, next ? `${next}T${time}` : null);
}

export const isMemoPage = async (store: Store, pageId: string) => {
  const folder = await store.getSetting(MEMOS_KEY);
  return !!folder && (await store.getPage(pageId))?.parentId === folder;
};
